import type { ReactNode } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { buttonVariants } from '@voll-intelligence/ui-kit/ui/button';
import { cn } from '@voll-intelligence/ui-kit/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@voll-intelligence/ui-kit/ui/select';

/**
 * Quem não tem o dado do critério vai para o FIM — e o fim é o fim NOS DOIS
 * sentidos, por isso a ausência é decidida antes da direção. Inverter os
 * argumentos para descer mandaria o ausente para o topo justamente nos
 * critérios "maior primeiro".
 *
 * `undefined` comparado com número devolve `NaN`, que o `sort` lê como "iguais"
 * e deixa a posição a cargo da ordem de entrada.
 */
export function compareOptional(
  a: number | undefined,
  b: number | undefined,
  direction: 'asc' | 'desc',
): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return direction === 'asc' ? a - b : b - a;
}

export type ResultsSortProps<Order extends string> = {
  readonly value: Order;
  readonly orders: readonly Order[];
  /** Nome acessível do gatilho ("Ordenar"). */
  readonly sortLabel: string;
  readonly orderLabel: (order: Order) => string;
  readonly disabled?: boolean;
  readonly onChange: (order: Order) => void;
};

/**
 * O ordenador das páginas de resultados — `Select` e não um menu próprio: a
 * escolha é ÚNICA e o gatilho precisa mostrar o critério ativo, que é o que o
 * primitivo já faz. Os critérios são do produto (hotel, carro); o controle é um.
 *
 * `ghost` porque a borda de campo competia com a moldura dos cards logo abaixo
 * (chamada do designer no protótipo).
 */
export function ResultsSort<Order extends string>({
  value,
  orders,
  sortLabel,
  orderLabel,
  disabled,
  onChange,
}: ResultsSortProps<Order>): ReactNode {
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next) => {
        onChange(next as Order);
      }}
    >
      <SelectTrigger
        size="sm"
        /* O gatilho VESTE o botão em vez de remontar o visual à mão: a borda de
           campo do `SelectTrigger` competia com a moldura dos cards logo abaixo,
           e o padding de fábrica faz um `ghost` (sem borda nem fundo em repouso)
           parecer maior que o vizinho. Mesmo caminho do ordenador do protótipo. */
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'sm' }),
          'iuk-results-sort w-fit shrink-0 gap-1 px-1.5 py-0',
          '[&_svg]:text-current [&_svg]:opacity-70',
        )}
        aria-label={sortLabel}
      >
        <ArrowUpDown aria-hidden="true" />
        {/* Filho explícito porque o `Select.Value` do Base UI imprime o VALOR, não
            o rótulo do item escolhido — o gatilho lia `recommended` em vez de
            `Recomendado`. Mesmo padrão do `hotel_rooms_field`. */}
        <SelectValue>{(order) => orderLabel(order as Order)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {orders.map((order) => (
          <SelectItem key={order} value={order}>
            {orderLabel(order)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
